import { useState, useEffect } from "react";
import axios from "axios";
import { sessionAssessments } from "@/data/assessmentData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const confidenceLevels = [
  { emoji: "😟", label: "Not confident yet", value: 1 },
  { emoji: "😐", label: "Getting there", value: 2 },
  { emoji: "🙂", label: "Confident", value: 3 },
  { emoji: "🚀", label: "Super confident", value: 4 }
];

// Session 7 specific confidence levels for pitch feelings
const pitchFeelingLevels = [
  { emoji: "😟", label: "Nervous", value: 1 },
  { emoji: "😐", label: "A little nervous but ready", value: 2 },
  { emoji: "🙂", label: "Confident", value: 3 },
  { emoji: "🚀", label: "Excited", value: 4 }
];

function SessionAssessment({ moduleId, sessionNumber, onComplete }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [knowledgeAnswers, setKnowledgeAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState({});
  const [confidenceLevel, setConfidenceLevel] = useState(null);
  const [reflections, setReflections] = useState({ r1: "", r2: "", r3: "" });
  const [submitting, setSubmitting] = useState(false);
  const [assessmentData, setAssessmentData] = useState(null);
  
  // Session 7 specific state
  const [pitchStrength, setPitchStrength] = useState(null);
  const [pitchFeeling, setPitchFeeling] = useState(null);
  const [pitchDeliveryMethod, setPitchDeliveryMethod] = useState(null);
  const [finalFeeling, setFinalFeeling] = useState(null);

  const isSession7 = sessionNumber === 7;

  useEffect(() => {
    // Load assessment data based on session number
    const assessmentKey = `session-${sessionNumber}`;
    const data = sessionAssessments[assessmentKey];
    if (data) {
      setAssessmentData(data);
    } else {
      console.error(`No assessment data found for ${assessmentKey}`);
    }
  }, [sessionNumber]);

  const handleKnowledgeAnswer = (questionId, answerId) => {
    setKnowledgeAnswers(prev => ({ ...prev, [questionId]: answerId }));
    setShowFeedback(prev => ({ ...prev, [questionId]: true }));
  };

  const canProceedFromKnowledge = () => {
    if (!assessmentData) return false;
    return assessmentData.knowledgeCheck.every(q => knowledgeAnswers[q.id]);
  };

  // Session 7: Check if pitch readiness is complete
  const canProceedFromPitchReadiness = () => {
    return pitchStrength && pitchFeeling;
  };

  // Session 7: Check if pitch delivery is selected
  const canProceedFromPitchDelivery = () => {
    return pitchDeliveryMethod;
  };

  const handleSubmit = async () => {
    // Validation for regular sessions
    if (!isSession7) {
      if (!confidenceLevel || !reflections.r1 || !reflections.r2 || !reflections.r3) {
        alert("Please complete all reflection questions before submitting.");
        return;
      }
    } else {
      // Validation for Session 7
      if (!reflections.r1 || !reflections.r2 || !finalFeeling) {
        alert("Please complete all reflection questions before submitting.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const submissionData = {
        module_id: moduleId,
        session_number: assessmentData.sessionNumber,
        knowledge_answers: knowledgeAnswers,
        confidence_level: isSession7 ? finalFeeling : confidenceLevel,
        reflections: reflections
      };

      // Add Session 7 specific data
      if (isSession7) {
        submissionData.pitch_strength = pitchStrength;
        submissionData.pitch_feeling = pitchFeeling;
        submissionData.pitch_delivery_method = pitchDeliveryMethod;
      }

      await axios.post(`${API}/assessments/submit`, submissionData);

      setCurrentStep(isSession7 ? 6 : 5); // Show completion (Session 7 has 6 steps)
      if (onComplete) {
        setTimeout(() => onComplete(), 3000);
      }
    } catch (err) {
      console.error("Error submitting assessment:", err);
      alert("Failed to submit assessment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!assessmentData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Loading assessment...</p>
      </div>
    );
  }

  // Calculate total steps based on session
  const totalSteps = isSession7 ? 5 : 4;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl shadow-xl p-8 mb-8 text-white">
        <div className="text-center">
          <div className="text-5xl mb-4">🎯</div>
          <h2 className="text-3xl font-bold mb-2">{assessmentData.sessionTitle}</h2>
          <h3 className="text-xl mb-4">
            {isSession7 ? "Prepare and Deliver Your Business Pitch" : "Session Reflection & Learning Check"}
          </h3>
          <p className="text-lg opacity-90">
            {isSession7 
              ? "This is not a test. This is a milestone." 
              : "This is not a test. This helps you grow and reflect on what you learned."}
          </p>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="flex justify-center mb-8">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
          <div key={step} className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                currentStep >= step
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-500"
              }`}
            >
              {step}
            </div>
            {step < totalSteps && (
              <div
                className={`w-16 h-1 ${
                  currentStep > step ? "bg-blue-500" : "bg-gray-200"
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* ============================================ */}
      {/* REGULAR SESSIONS (1-6) */}
      {/* ============================================ */}
      
      {!isSession7 && (
        <>
          {/* Step 1: Knowledge Check */}
          {currentStep === 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  ✅ Part 1: Knowledge Check
                </h3>
                <p className="text-gray-600">
                  Answer the questions below. This helps you practice what you learned.
                </p>
              </div>

              {assessmentData.knowledgeCheck.map((question, index) => (
                <div key={question.id} className="mb-8 p-6 bg-gray-50 rounded-xl">
                  <p className="font-semibold text-lg text-gray-800 mb-4">
                    Question {index + 1}: {question.question}
                  </p>

                  <div className="space-y-3">
                    {question.options.map((option) => {
                      const isSelected = knowledgeAnswers[question.id] === option.id;
                      const isCorrect = option.id === question.correctAnswer;
                      const showResult = showFeedback[question.id];

                      return (
                        <button
                          key={option.id}
                          onClick={() => handleKnowledgeAnswer(question.id, option.id)}
                          disabled={showFeedback[question.id]}
                          className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                            !showResult
                              ? isSelected
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-300 bg-white hover:border-blue-300"
                              : isCorrect
                              ? "border-green-500 bg-green-50"
                              : isSelected
                              ? "border-red-300 bg-red-50"
                              : "border-gray-300 bg-white opacity-50"
                          }`}
                        >
                          <span className="font-medium">{option.id}.</span> {option.text}
                          {showResult && isCorrect && <span className="ml-2">✅</span>}
                        </button>
                      );
                    })}
                  </div>

                  {showFeedback[question.id] && (
                    <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                      <p className="text-blue-800">
                        <strong>Learning Point:</strong> {question.feedback}
                      </p>
                    </div>
                  )}
                </div>
              ))}

              <div className="text-center mt-8">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={!canProceedFromKnowledge()}
                  className={`px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
                    canProceedFromKnowledge()
                      ? "bg-blue-500 text-white hover:bg-blue-600 hover:shadow-xl"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Continue to Confidence Check →
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Confidence Check */}
          {currentStep === 2 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  💪 Part 2: Confidence Self-Check
                </h3>
                <p className="text-gray-600">{assessmentData.confidencePrompt}</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {confidenceLevels.map((level) => (
                  <button
                    key={level.value}
                    onClick={() => setConfidenceLevel(level.value)}
                    className={`p-6 rounded-xl border-4 transition-all transform hover:scale-105 ${
                      confidenceLevel === level.value
                        ? "border-purple-500 bg-purple-50 shadow-lg"
                        : "border-gray-200 bg-white hover:border-purple-300"
                    }`}
                  >
                    <div className="text-5xl mb-2">{level.emoji}</div>
                    <p className="text-sm font-semibold text-gray-700">{level.label}</p>
                  </button>
                ))}
              </div>

              {confidenceLevel && (
                <div className="p-4 bg-purple-50 border-l-4 border-purple-500 rounded mb-6">
                  <p className="text-purple-800">
                    <strong>LemonBot says:</strong> {assessmentData.confidenceAutoResponse || "Confidence grows with practice. Wherever you are is exactly where you should be."}
                  </p>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  disabled={!confidenceLevel}
                  className={`flex-1 px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
                    confidenceLevel
                      ? "bg-purple-500 text-white hover:bg-purple-600 hover:shadow-xl"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Continue to Reflection →
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Reflection */}
          {currentStep === 3 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  💭 Part 3: Reflection
                </h3>
                <p className="text-gray-600">
                  There are no wrong answers. Be honest and thoughtful.
                </p>
              </div>

              {assessmentData.reflectionQuestions.map((question, index) => (
                <div key={question.id} className="mb-6">
                  <label className="block font-semibold text-gray-800 mb-2">
                    {index + 1}. {question.question}
                  </label>
                  <textarea
                    value={reflections[question.id]}
                    onChange={(e) =>
                      setReflections(prev => ({ ...prev, [question.id]: e.target.value }))
                    }
                    className="w-full p-4 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                    rows="4"
                    placeholder="Type your answer here..."
                  />
                </div>
              ))}

              <div className="flex gap-4">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setCurrentStep(4)}
                  disabled={!reflections.r1 || !reflections.r2 || !reflections.r3}
                  className={`flex-1 px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
                    reflections.r1 && reflections.r2 && reflections.r3
                      ? "bg-green-500 text-white hover:bg-green-600 hover:shadow-xl"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Review & Submit →
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Review & Submit */}
          {currentStep === 4 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  🎉 Part 4: Review & Submit
                </h3>
                <p className="text-gray-600">
                  Great work! Review your responses and submit when ready.
                </p>
              </div>

              <div className="space-y-6 mb-8">
                <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded">
                  <p className="font-semibold text-green-800">✓ Knowledge Check Completed</p>
                </div>
                <div className="p-4 bg-purple-50 border-l-4 border-purple-500 rounded">
                  <p className="font-semibold text-purple-800">
                    ✓ Confidence Level: {confidenceLevels[confidenceLevel - 1]?.label}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                  <p className="font-semibold text-blue-800">✓ Reflections Completed</p>
                </div>
              </div>

              <div className="p-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl mb-6">
                <div className="flex items-start gap-3">
                  <div className="text-3xl">🍋</div>
                  <div>
                    <p className="font-semibold text-gray-800 mb-2">LemonBot says:</p>
                    <p className="text-gray-700">{assessmentData.lemonBotMessage}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  ← Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-green-500 to-blue-500 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                >
                  {submitting ? "Submitting..." : "Submit Reflection ✓"}
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Completion */}
          {currentStep === 5 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="text-7xl mb-4">🎉</div>
              <h3 className="text-3xl font-bold text-gray-800 mb-4">
                Session Reflection Submitted!
              </h3>
              <p className="text-xl text-gray-600 mb-6">
                You've completed {assessmentData.sessionTitle}
              </p>
              <div className="inline-block bg-green-100 text-green-700 px-6 py-3 rounded-full font-semibold">
                ✓ Session Complete
              </div>
            </div>
          )}
        </>
      )}

      {/* ============================================ */}
      {/* SESSION 7: PITCH DAY SPECIAL FLOW */}
      {/* ============================================ */}
      
      {isSession7 && (
        <>
          {/* Step 1: Pitch Readiness Check */}
          {currentStep === 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  🎤 Part 1: Pitch Readiness Check
                </h3>
                <p className="text-gray-600">
                  Before you present, take a moment to check in with yourself.
                </p>
              </div>

              {/* Question 1: Pitch Strength */}
              <div className="mb-8 p-6 bg-gray-50 rounded-xl">
                <p className="font-semibold text-lg text-gray-800 mb-4">
                  Which part of your pitch do you feel strongest about?
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {["The hook", "The problem", "The solution", "What makes us special", "The ask"].map((option) => (
                    <button
                      key={option}
                      onClick={() => setPitchStrength(option)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        pitchStrength === option
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300 bg-white hover:border-blue-300"
                      }`}
                    >
                      <span className="font-medium">☐</span> {option}
                    </button>
                  ))}
                </div>
                {pitchStrength && (
                  <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                    <p className="text-blue-800">
                      <strong>LemonBot says:</strong> Knowing your strengths helps you pitch with confidence.
                    </p>
                  </div>
                )}
              </div>

              {/* Question 2: Pitch Feeling */}
              <div className="mb-8 p-6 bg-gray-50 rounded-xl">
                <p className="font-semibold text-lg text-gray-800 mb-4">
                  How do you feel about presenting your pitch today?
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {pitchFeelingLevels.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setPitchFeeling(level.value)}
                      className={`p-6 rounded-xl border-4 transition-all transform hover:scale-105 ${
                        pitchFeeling === level.value
                          ? "border-purple-500 bg-purple-50 shadow-lg"
                          : "border-gray-200 bg-white hover:border-purple-300"
                      }`}
                    >
                      <div className="text-4xl mb-2">{level.emoji}</div>
                      <p className="text-sm font-semibold text-gray-700">{level.label}</p>
                    </button>
                  ))}
                </div>
                {pitchFeeling && (
                  <div className="mt-4 p-4 bg-purple-50 border-l-4 border-purple-500 rounded">
                    <p className="text-purple-800">
                      <strong>LemonBot says:</strong> Nerves mean you care. Confidence grows when you try.
                    </p>
                  </div>
                )}
              </div>

              <div className="text-center mt-8">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={!canProceedFromPitchReadiness()}
                  className={`px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
                    canProceedFromPitchReadiness()
                      ? "bg-blue-500 text-white hover:bg-blue-600 hover:shadow-xl"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Continue to Knowledge Check →
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Knowledge Check (Quick Review) */}
          {currentStep === 2 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  ✅ Part 2: Knowledge Check (Quick Review)
                </h3>
                <p className="text-gray-600">
                  These questions help you remember the key parts of a great pitch.
                </p>
              </div>

              {assessmentData.knowledgeCheck.map((question, index) => (
                <div key={question.id} className="mb-8 p-6 bg-gray-50 rounded-xl">
                  <p className="font-semibold text-lg text-gray-800 mb-4">
                    Question {index + 1}: {question.question}
                  </p>

                  <div className="space-y-3">
                    {question.options.map((option) => {
                      const isSelected = knowledgeAnswers[question.id] === option.id;
                      const isCorrect = option.id === question.correctAnswer;
                      const showResult = showFeedback[question.id];

                      return (
                        <button
                          key={option.id}
                          onClick={() => handleKnowledgeAnswer(question.id, option.id)}
                          disabled={showFeedback[question.id]}
                          className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                            !showResult
                              ? isSelected
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-300 bg-white hover:border-blue-300"
                              : isCorrect
                              ? "border-green-500 bg-green-50"
                              : isSelected
                              ? "border-red-300 bg-red-50"
                              : "border-gray-300 bg-white opacity-50"
                          }`}
                        >
                          <span className="font-medium">{option.id}.</span> {option.text}
                          {showResult && isCorrect && <span className="ml-2">✅</span>}
                        </button>
                      );
                    })}
                  </div>

                  {showFeedback[question.id] && (
                    <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                      <p className="text-blue-800">
                        <strong>Learning Point:</strong> {question.feedback}
                      </p>
                    </div>
                  )}
                </div>
              ))}

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  disabled={!canProceedFromKnowledge()}
                  className={`flex-1 px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
                    canProceedFromKnowledge()
                      ? "bg-blue-500 text-white hover:bg-blue-600 hover:shadow-xl"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Continue to Pitch Delivery →
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Pitch Delivery */}
          {currentStep === 3 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  🎬 Part 3: Pitch Delivery
                </h3>
                <p className="text-gray-600">
                  Now it's time to share your idea. Choose how you will present your pitch.
                </p>
              </div>

              <div className="text-center mb-8">
                <p className="text-xl font-semibold text-gray-700 mb-6">This is your moment.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { id: "live", label: "Live in front of class", icon: "🎤" },
                    { id: "recorded", label: "Recorded video", icon: "📹" },
                    { id: "small-group", label: "Small group presentation", icon: "👥" }
                  ].map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setPitchDeliveryMethod(option.id)}
                      className={`p-6 rounded-xl border-4 transition-all transform hover:scale-105 ${
                        pitchDeliveryMethod === option.id
                          ? "border-green-500 bg-green-50 shadow-lg"
                          : "border-gray-200 bg-white hover:border-green-300"
                      }`}
                    >
                      <div className="text-5xl mb-3">{option.icon}</div>
                      <p className="font-semibold text-gray-700">{option.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {pitchDeliveryMethod && (
                <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded mb-6">
                  <p className="text-green-800">
                    <strong>Great choice!</strong> Remember: confidence and effort matter more than perfection.
                  </p>
                </div>
              )}

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setCurrentStep(4)}
                  disabled={!canProceedFromPitchDelivery()}
                  className={`flex-1 px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
                    canProceedFromPitchDelivery()
                      ? "bg-green-500 text-white hover:bg-green-600 hover:shadow-xl"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Continue to Reflection →
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Pitch Reflection (After the Pitch) */}
          {currentStep === 4 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  💭 Part 4: Pitch Reflection
                </h3>
                <p className="text-gray-600">
                  After presenting, take a moment to reflect on your experience.
                </p>
              </div>

              {/* Reflection Question 1 */}
              <div className="mb-6">
                <label className="block font-semibold text-gray-800 mb-2">
                  1. What part of your pitch are you most proud of?
                </label>
                <textarea
                  value={reflections.r1}
                  onChange={(e) => setReflections(prev => ({ ...prev, r1: e.target.value }))}
                  className="w-full p-4 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  rows="4"
                  placeholder="Type your answer here..."
                />
              </div>

              {/* Reflection Question 2 */}
              <div className="mb-6">
                <label className="block font-semibold text-gray-800 mb-2">
                  2. What is one thing you would improve next time?
                </label>
                <textarea
                  value={reflections.r2}
                  onChange={(e) => setReflections(prev => ({ ...prev, r2: e.target.value }))}
                  className="w-full p-4 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  rows="4"
                  placeholder="Type your answer here..."
                />
              </div>

              {/* Reflection Question 3: Final Feeling (Emoji Scale) */}
              <div className="mb-6">
                <label className="block font-semibold text-gray-800 mb-4">
                  3. How do you feel about yourself after completing this program?
                </label>
                <div className="grid grid-cols-4 gap-4">
                  {confidenceLevels.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setFinalFeeling(level.value)}
                      className={`p-4 rounded-xl border-4 transition-all transform hover:scale-105 ${
                        finalFeeling === level.value
                          ? "border-purple-500 bg-purple-50 shadow-lg"
                          : "border-gray-200 bg-white hover:border-purple-300"
                      }`}
                    >
                      <div className="text-4xl">{level.emoji}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setCurrentStep(5)}
                  disabled={!reflections.r1 || !reflections.r2 || !finalFeeling}
                  className={`flex-1 px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
                    reflections.r1 && reflections.r2 && finalFeeling
                      ? "bg-purple-500 text-white hover:bg-purple-600 hover:shadow-xl"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Continue to Celebration →
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Review & Submit with Celebration */}
          {currentStep === 5 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  🎉 Part 5: Celebration & Submit
                </h3>
                <p className="text-gray-600">
                  You've completed everything! Review and celebrate your achievement.
                </p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded">
                  <p className="font-semibold text-green-800">✓ Pitch Readiness Check Completed</p>
                </div>
                <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                  <p className="font-semibold text-blue-800">✓ Knowledge Check Completed</p>
                </div>
                <div className="p-4 bg-purple-50 border-l-4 border-purple-500 rounded">
                  <p className="font-semibold text-purple-800">✓ Pitch Delivered</p>
                </div>
                <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
                  <p className="font-semibold text-yellow-800">✓ Reflection Completed</p>
                </div>
              </div>

              {/* LemonBot Celebration Message */}
              <div className="p-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl mb-6">
                <div className="flex items-start gap-3">
                  <div className="text-3xl">🍋</div>
                  <div>
                    <p className="font-semibold text-gray-800 mb-2">LemonBot says:</p>
                    <p className="text-gray-700">{assessmentData.lemonBotMessage}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setCurrentStep(4)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  ← Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-green-500 to-blue-500 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                >
                  {submitting ? "Submitting..." : "Complete Program 🎉"}
                </button>
              </div>
            </div>
          )}

          {/* Step 6: Final Celebration & Certificate */}
          {currentStep === 6 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="text-7xl mb-4">🏆</div>
              <h3 className="text-3xl font-bold text-gray-800 mb-4">
                Congratulations!
              </h3>
              <p className="text-xl text-gray-600 mb-6">
                You've completed the Lemonade Stand Business Plan Program!
              </p>
              
              {/* Certificate Preview */}
              <div className="bg-gradient-to-r from-yellow-100 to-orange-100 border-4 border-yellow-400 rounded-2xl p-8 mb-8 max-w-lg mx-auto">
                <div className="text-4xl mb-4">🍋</div>
                <h4 className="text-2xl font-bold text-gray-800 mb-2">
                  {assessmentData.certificateTitle || "Certified Young Entrepreneur"}
                </h4>
                <p className="text-gray-600 italic">
                  {assessmentData.certificateMessage || "This certificate recognizes your creativity, teamwork, confidence, and entrepreneurial mindset."}
                </p>
              </div>

              <div className="space-y-4">
                <div className="inline-block bg-green-100 text-green-700 px-6 py-3 rounded-full font-semibold">
                  ✓ Program Complete
                </div>
                <p className="text-gray-500">
                  You will be redirected to your certificate shortly...
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SessionAssessment;
